import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import './BookingSuccess.css';

const BookingSuccess = ({ bookingData, onClose }) => {
  const { t } = useTranslation();
  
  // Make sure we can access data safely
  const data = bookingData?.data || {};
  const bookingDetails = data?.booking_details || {};
  const friendBookingDetails = data?.friend_booking_details || null;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      const date = new Date(dateStr);
      const locale = t('date_locale') || 'en-US';
      
      // Use the proper locale from translations to get date and time in the correct language
      // Added timeZone: 'UTC' to prevent timezone conversion
      return date.toLocaleString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC' // This forces display in UTC regardless of user's timezone
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr; // Return original string as fallback
    }
  };

  // Content for the success modal
  const successModalContent = (
    <div className="booking-success-overlay">
      <div className="booking-success-modal">
        <button className="close-button" onClick={onClose}>×</button>
        
        <div className="success-icon">✓</div>
        <h2>{t('booking_confirmed')}</h2>
        <p className="success-message">{t('thank_you_booking')}</p>
        
        <div className="booking-summary">
          <h3>{friendBookingDetails ? t('your_booking_details') : t('booking_details')}</h3>
          <div className="summary-item">
            <span>{t('class')}</span>
            <span>{bookingDetails.class_name || 'Yoga Class'}</span>
          </div>
          
          <div className="summary-item">
            <span>{t('date_time')}</span>
            <span>{formatDate(bookingDetails.start_time)}</span>
          </div>
          
          <div className="summary-item">
            <span>{t('location')}</span>
            <span>{bookingDetails.location || 'Main Studio'}</span>
          </div>
          
          <div className="summary-item">
            <span>{t('instructor')}</span>
            <span>{bookingDetails.instructor_name || 'Staff'}</span>
          </div>
          
          <div className="summary-item">
            <span>{t('number_of_spots')}</span>
            <span>{data.quantity || 1}</span>
          </div>
          
          <div className="summary-item">
            <span>{t('booking_reference')}</span>
            <span>#{bookingDetails.booking_id || data.bookingId || 'N/A'}</span>
          </div>
          
          {bookingDetails.special_requests && (
            <div className="summary-item">
              <span>{t('special_requests')}</span>
              <span>{bookingDetails.special_requests}</span>
            </div>
          )}
        </div>
        
        {friendBookingDetails && (
          <div className="booking-summary friend-booking-summary">
            <h3>{t('friend_booking_details')}</h3>
            <div className="summary-item">
              <span>{t('name')}</span>
              <span>{friendBookingDetails.client_name || 'Friend'}</span>
            </div>
            
            <div className="summary-item">
              <span>{t('email')}</span>
              <span>{friendBookingDetails.client_email || 'N/A'}</span>
            </div>
            
            <div className="summary-item">
              <span>{t('class')}</span>
              <span>{friendBookingDetails.class_name || 'Yoga Class'}</span>
            </div>
            
            <div className="summary-item">
              <span>{t('date_time')}</span>
              <span>{formatDate(friendBookingDetails.start_time)}</span>
            </div>
            
            <div className="summary-item">
              <span>{t('booking_reference')}</span>
              <span>#{friendBookingDetails.booking_id || 'N/A'}</span>
            </div>
            
            {friendBookingDetails.special_requests && (
              <div className="summary-item">
                <span>{t('special_requests')}</span>
                <span>{friendBookingDetails.special_requests}</span>
              </div>
            )}
          </div>
        )}
        
        <p className="confirmation-note">
          {friendBookingDetails 
            ? t('confirmation_emails_sent') 
            : t('confirmation_email_sent')}
        </p>
        
        <div className="success-actions">
          <button className="close-success-button" onClick={onClose}>
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render the modal outside of the normal DOM hierarchy
  return ReactDOM.createPortal(
    successModalContent,
    document.getElementById('popup-root')
  );
};

export default BookingSuccess; 